import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { getSetting, subscribe } from '../db/db';
import { SEASON_SETTING, normStartMonth } from '../db/season';

// ---------------------------------------------------------------------------
// Směrování přes #hash – funguje offline i na GitHub Pages bez serveru.
// ---------------------------------------------------------------------------

export interface Route {
  parts: string[];
  query: URLSearchParams;
}

function parseHash(): Route {
  const raw = location.hash.replace(/^#\/?/, '');
  const [path, qs = ''] = raw.split('?');
  return { parts: path.split('/').filter(Boolean).map(decodeURIComponent), query: new URLSearchParams(qs) };
}

const subscribeHash = (cb: () => void) => {
  addEventListener('hashchange', cb);
  return () => removeEventListener('hashchange', cb);
};

let lastHash: string | null = null;
let lastRoute: Route = parseHash();

export function useRoute(): Route {
  const hash = useSyncExternalStore(subscribeHash, () => location.hash);
  if (hash !== lastHash) {
    lastHash = hash;
    lastRoute = parseHash();
  }
  return lastRoute;
}

/** Počet přechodů uvnitř aplikace – podle něj víme, jestli je kam se vracet. */
let depth = 0;

// Pozice posunu stránky: při cestě dál se nová obrazovka otevře nahoře,
// při návratu zpět se obnoví místo, kde uživatel byl (i když se obsah načítá
// postupně). Bez toho na iPhonu po návratu z dlouhé obrazovky zůstávala
// černá plocha, dokud se displeje nikdo nedotkl.
const positions = new Map<string, number>();
// pozici obnovujeme sami – automatika prohlížeče by se s tím přetahovala
try {
  history.scrollRestoration = 'manual';
} catch {
  /* nevadí */
}
let navKind: 'forward' | 'back' | null = null;

export function go(path: string, replace = false) {
  const url = '#/' + path.replace(/^\//, '');
  positions.set(location.hash, scrollY);
  navKind = 'forward';
  if (replace) location.replace(url);
  else {
    depth++;
    location.hash = url;
  }
}

export function back(fallback = '') {
  if (depth > 0) {
    depth--;
    navKind = 'back';
    history.back();
  } else {
    go(fallback, true);
  }
}

/** Po změně obrazovky: nahoru, nebo (při návratu) na uložené místo. */
export function restoreScroll(hash: string) {
  const kind = navKind ?? 'back'; // tlačítko Zpět v Androidu / gesto = návrat
  navKind = null;
  const target = kind === 'back' ? positions.get(hash) ?? 0 : 0;
  // Obsah nad místem se může dočíst až po posunu a Chrome (ukotvení posunu) pak
  // stránkou sám odjede jinam – místo proto hlídáme, dokud se výška stránky
  // neustálí. Jakmile uživatel sám sáhne na displej, přestaneme.
  const run = ++restoreRun;
  let frames = 0;
  let stable = 0;
  let lastH = -1;
  const stop = () => {
    if (restoreRun === run) restoreRun++;
  };
  const events = ['touchstart', 'wheel', 'keydown', 'mousedown'] as const;
  events.forEach((e) => addEventListener(e, stop, { passive: true }));
  const step = () => {
    const h = document.documentElement.scrollHeight;
    if (restoreRun === run) {
      const y = Math.min(target, Math.max(0, h - innerHeight));
      if (Math.abs(scrollY - y) > 1) scrollTo(0, y);
      stable = h === lastH && h - innerHeight >= target - 2 ? stable + 1 : 0;
      lastH = h;
    }
    if (restoreRun === run && stable < 10 && frames++ < 120) requestAnimationFrame(step);
    else events.forEach((e) => removeEventListener(e, stop));
  };
  requestAnimationFrame(step);
}
let restoreRun = 0;

// ---------------------------------------------------------------------------
// Dotaz do databáze, který se sám obnoví po každé změně dat.
// ---------------------------------------------------------------------------

export function useQuery<T>(fn: () => Promise<T>, deps: unknown[]): T | undefined {
  const [value, setValue] = useState<T | undefined>(undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fn, deps);
  useEffect(() => {
    let alive = true;
    const load = () =>
      run().then(
        (v) => alive && setValue(() => v),
        (e) => console.error(e),
      );
    load();
    const unsub = subscribe(load);
    return () => {
      alive = false;
      unsub();
    };
  }, [run]);
  return value;
}

/** Měsíc začátku včelařského roku z Nastavení (1 = kalendářní rok). */
export function useSeasonStart(): number | undefined {
  return useQuery(async () => normStartMonth((await getSetting<number>(SEASON_SETTING)) ?? 1), []);
}
